<template>
  <div>
    <v-form ref="candidateForm" v-model="isFormValid" lazy-validation>
      <v-row>
        <v-col cols="12" sm="6">
          <v-btn
            block
            min-height="200px"
            :outlined="newCandidateOutlined"
            color="primary"
            style="font-size: 30px"
            @click="newCandidateClickedHandler"
          >
            New<br>Candidate
          </v-btn>
        </v-col>
        <v-col cols="12" sm="6">
          <v-btn
            block
            min-height="200px"
            :outlined="existingCandidateOutlined"
            color="primary"
            style="font-size: 30px"
            @click="existingCandidateClickedHandler"
          >
            Existing<br>Candidate
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="6" lg="3" v-if="isExistingCandidate">
          <UsersDropDownList
            :userId="existingUserId"
            :isRequired="isExistingCandidate"
            :readonly="readonly"
            label="Consultant"
            :securityRoles="[CONSULTANT]"
            @userIdChange="userIdChangeHandler"
          ></UsersDropDownList>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            label="First Name"
            v-model="firstName"
            :rules="[Required('First Name')]"
            :readonly="isExistingCandidate || readonly"
            :disabled="!candidateType"
            outlined
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            label="Last Name"
            v-model="lastName"
            :rules="[Required('Last Name')]"
            :readonly="isExistingCandidate || readonly"
            :disabled="!candidateType"
            outlined
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <EmailInput
            :label="'Login E-Mail'"
            :email-address="emailAddress"
            :is-required="true"
            :readOnly="isExistingCandidate || readonly"
            :disabled="!candidateType"
            @emailChange="(newValue) => this.emailAddress = newValue"
          ></EmailInput>
        </v-col>
      </v-row>
      <ButtonsSubmitForm
        :acceptLabel="nextButtonLabel"
        cancelLabel="back"
        :isFormValid="true"
        @save="nextStep"
        @cancel="goBack"
      >
      </ButtonsSubmitForm>
      <MessageBox
        ref="messageBox"
        :message="message"
        color="error"
        closeTextColor="white"
      ></MessageBox>
    </v-form>
  </div>
</template>

<script>
import { ButtonsSubmitForm } from '@/components/buttons'
import { UsersDropDownList } from '@/components/users'
import Required from '@/lib/formValidations/required'
import { EmailInput } from '@/components/emails'
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { CREATE_FROM_ZOHO } from '@/lib/constants/dealCreationTypes'
import { NEW_CANDIDATE, EXISTING_CANDIDATE } from '@/lib/constants/candidateTypes'
import { CONSULTANT } from '@/lib/constants/securityRoles'
import { MessageBox } from '@/components/shared/messages'

export default {
  props: [
    'readonly'
  ],
  components: {
    ButtonsSubmitForm,
    UsersDropDownList,
    EmailInput,
    MessageBox
  },
  data () {
    return {
      NEW_CANDIDATE,
      EXISTING_CANDIDATE,
      newCandidateOutlined: true,
      existingCandidateOutlined: true,
      isFormValid: null,
      CREATE_FROM_ZOHO,
      CONSULTANT,
      message: null
    }
  },
  computed: {
    ...mapFields('deal', ['id', 'statusId', 'dealCreationTypeId', 'dealCandidateId']),
    ...mapFields('dealCandidate',
      [
        'dealId',
        'existingUserId',
        'firstName',
        'lastName',
        'emailAddress',
        'candidateType'
      ]),
    isExistingCandidate () {
      return this.candidateType === this.EXISTING_CANDIDATE
    },
    nextButtonLabel () {
      return (!this.statusId && this.dealCreationTypeId === CREATE_FROM_ZOHO) || this.readonly ? 'next' : 'save'
    }
  },
  watch: {
    candidateType () {
      if (this.candidateType === this.NEW_CANDIDATE) {
        this.newCandidateOutlined = false
        this.existingCandidateOutlined = true
      }
      if (this.candidateType === this.EXISTING_CANDIDATE) {
        this.newCandidateOutlined = true
        this.existingCandidateOutlined = false
      }
    }
  },
  methods: {
    ...mapActions('user', ['getUser']),
    ...mapActions('dealCandidate', ['saveDealCandidate']),
    Required,
    newCandidateClickedHandler () {
      if (!this.readonly) {
        this.candidateType = this.NEW_CANDIDATE
        this.clearCandidate()
      }
      this.$refs.candidateForm.resetValidation()
    },
    existingCandidateClickedHandler () {
      if (!this.readonly) {
        this.candidateType = this.EXISTING_CANDIDATE
        this.clearCandidate()
      }
      this.$refs.candidateForm.resetValidation()
    },
    nextStep () {
      if (!this.readonly && !!this.statusId) {
        if (!this.candidateType) {
          this.message = 'Please choose an existing candidate or create a new one'
          this.$refs.messageBox.show()
        } else if (this.candidateType === NEW_CANDIDATE &&
        !this.firstName &&
        !this.lastName &&
        !this.emailAddress
        ) {
          this.message = 'There are required fields missing'
          this.$refs.candidateForm.validate()
          this.$refs.messageBox.show()
        } else if (this.candidateType === EXISTING_CANDIDATE && !this.existingUserId) {
          this.message = 'Please choose an existing candidate to continue'
          this.$refs.candidateForm.validate()
          this.$refs.messageBox.show()
        } else if (this.$refs.candidateForm.validate()) {
          this.dealId = this.id
          this.saveDealCandidate().then((response) => {
            this.dealCandidateId = response.data.id
            this.$emit('complete')
          })
        }
      } else {
        this.$emit('complete')
      }
    },
    goBack () {
      this.$emit('goBack')
    },
    userIdChangeHandler (newUserId) {
      this.existingUserId = newUserId
      this.getUser({ id: this.existingUserId }).then(data => {
        this.firstName = data.firstName
        this.lastName = data.lastName
        this.emailAddress = data.emailAddress
      })
    },
    clearCandidate () {
      this.existingUserId = null
      this.firstName = null
      this.lastName = null
      this.emailAddress = null
    }
  }
}
</script>
